import { useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import type { DateValidationError } from '@mui/x-date-pickers/models'
import dayjs, { type Dayjs } from 'dayjs'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import { useTranslation } from 'react-i18next'

import type { AssignedTheme } from '@/api/programs'
import { toDateYmd, todayYmd } from '@/utils/date'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'

/**
 * Самый ранний день прохождения, который примет сервер: момент прохождения
 * хранится в TIMESTAMP (см. CompleteThemeRequest::EARLIEST_COMPLETION_DATE
 * в v3.api.easya). Держать синхронно с API.
 */
const EARLIEST_COMPLETION_DATE = '1970-01-02'

/**
 * complete — первичная отметка темы пройденной;
 * change — исправление даты у уже пройденной темы.
 */
export type ThemeCompletionMode = 'complete' | 'change'

interface ThemeCompletionDateDialogProps {
  open: boolean
  /** Тема держится до конца анимации закрытия — см. onExited */
  theme: AssignedTheme | null
  mode: ThemeCompletionMode
  onClose: () => void
  /** Анимация закрытия закончилась: теперь тему можно сбросить */
  onExited: () => void
  /**
   * completedAt — выбранный день `YYYY-MM-DD` или undefined, если при первичной
   * отметке оставлен сегодняшний: тогда запрос уходит без поля, как раньше.
   * Ошибку запроса диалог показывает сам, закрывает его вызывающий.
   */
  onSubmit: (completedAt: string | undefined) => Promise<void>
}

/**
 * Выбор дня прохождения темы назначения.
 *
 * Учитель переносит в платформу историю давнего ученика: тему прошли
 * в марте, а отмечают её сегодня. Будущие дни и дни раньше
 * EARLIEST_COMPLETION_DATE недоступны — сервер их не примет. «Сегодня»
 * считается по часовому поясу расписания: в нём же сервер проверяет границу
 * дня и в нём показывается дата прохождения.
 */
export function ThemeCompletionDateDialog({
  open,
  theme,
  mode,
  onClose,
  onExited,
  onSubmit,
}: ThemeCompletionDateDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      TransitionProps={{ onExited }}
    >
      {/* Содержимое монтируется заново на каждое открытие — черновик даты
          и ошибка прошлого раза не переживают закрытие */}
      {theme && <DialogBody theme={theme} mode={mode} onClose={onClose} onSubmit={onSubmit} />}
    </Dialog>
  )
}

function DialogBody({
  theme,
  mode,
  onClose,
  onSubmit,
}: Pick<ThemeCompletionDateDialogProps, 'mode' | 'onClose' | 'onSubmit'> & { theme: AssignedTheme }) {
  const { t, i18n } = useTranslation()
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'

  // Снимки на момент открытия: полночь посреди открытого диалога не должна
  // менять ни границу, ни «исходную» дату
  const [today] = useState(todayYmd)
  const [maxDate] = useState(() => dayjs(today))
  const [minDate] = useState(() => dayjs(EARLIEST_COMPLETION_DATE))
  // Текущий день прохождения — только при исправлении и только если он известен
  const [currentDate] = useState(() =>
    mode === 'change' && theme.completed_at ? toDateYmd(theme.completed_at) : null,
  )
  const [value, setValue] = useState<Dayjs | null>(() => dayjs(currentDate ?? today))
  const [validationError, setValidationError] = useState<DateValidationError | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selected = value !== null && value.isValid() ? value.format('YYYY-MM-DD') : null
  // Строки YYYY-MM-DD сравниваются лексикографически так же, как даты
  const canSubmit =
    selected !== null &&
    selected <= today &&
    selected >= EARLIEST_COMPLETION_DATE &&
    validationError === null &&
    selected !== currentDate

  const helperText =
    validationError === 'maxDate' || validationError === 'disableFuture'
      ? t('programs.completionDialog.futureError')
      : validationError === 'minDate'
        ? // Формат 'L' — тот же, что в поле ввода выбранного языка
          t('programs.completionDialog.tooEarlyError', { date: minDate.locale(adapterLocale).format('L') })
        : validationError !== null
          ? t('programs.completionDialog.invalidError')
          : undefined

  const handleSubmit = async () => {
    if (!canSubmit || selected === null) return

    setSubmitting(true)
    setError(null)
    try {
      // Сегодняшний день при первичной отметке — прежний запрос без тела.
      // При исправлении день уходит всегда: запрос без дня у пройденной темы
      // сдвинул бы закладку, а не дату.
      await onSubmit(mode === 'complete' && selected === today ? undefined : selected)
    } catch (err) {
      // Сеть, 5xx и таймауты приходят техническим текстом axios — его не показываем
      setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
      setSubmitting(false)
    }
  }

  return (
    <>
      <DialogTitle>
        {mode === 'complete'
          ? t('programs.completionDialog.completeTitle')
          : t('programs.completionDialog.changeTitle')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <DialogContentText sx={{ mb: 2 }}>
          {t('programs.completionDialog.theme', { title: theme.title ?? '' })}
        </DialogContentText>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
          <DatePicker
            label={t('programs.completionDialog.date')}
            value={value}
            onChange={(next) => setValue(next)}
            minDate={minDate}
            maxDate={maxDate}
            onError={(reason) => setValidationError(reason)}
            disabled={submitting}
            slotProps={{
              textField: {
                fullWidth: true,
                error: validationError !== null,
                helperText,
              },
            }}
          />
        </LocalizationProvider>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {mode === 'complete'
            ? t('programs.completionDialog.completeHint')
            : t('programs.completionDialog.changeHint')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() => void handleSubmit()}
        >
          {mode === 'complete' ? t('programs.completionDialog.completeAction') : t('common.save')}
        </Button>
      </DialogActions>
    </>
  )
}
